/* ==========================================================================
   TYLPS — JavaScript global
   Animations au défilement, parallaxe, en-tête, tiroirs, panier AJAX,
   accordéons, carrousels, newsletter, vidéos, compatibilité éditeur de thème.
   Aucun framework, aucune dépendance.
   ========================================================================== */
(() => {
  'use strict';

  const T = (window.TYLPS = window.TYLPS || {});
  T.strings = T.strings || {};
  T.routes = T.routes || { root: '/', cart: '/cart', cartAdd: '/cart/add', cartChange: '/cart/change' };

  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const canHover = () => window.matchMedia('(hover: hover)').matches;
  const animOn = () => document.body.classList.contains('tylps-anim') && !reduceMotion;

  /* ---------- Format monétaire (identique au format de la boutique) ---------- */
  T.formatMoney = (cents, format) => {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const fmt = format || T.moneyFormat || '{{amount}} €';
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const withDelims = (n, precision, thousands = ',', decimal = '.') => {
      if (isNaN(n) || n == null) return '0';
      const fixed = (n / 100).toFixed(precision);
      const [ints, decs] = fixed.split('.');
      return ints.replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`) + (decs ? decimal + decs : '');
    };
    const match = fmt.match(placeholder);
    if (!match) return fmt;
    let value;
    switch (match[1]) {
      case 'amount': value = withDelims(cents, 2); break;
      case 'amount_no_decimals': value = withDelims(cents, 0); break;
      case 'amount_with_comma_separator': value = withDelims(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelims(cents, 0, '.', ','); break;
      case 'amount_with_space_separator': value = withDelims(cents, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': value = withDelims(cents, 0, ' ', ''); break;
      case 'amount_with_apostrophe_separator': value = withDelims(cents, 2, "'", '.'); break;
      default: value = withDelims(cents, 2);
    }
    return fmt.replace(placeholder, value);
  };

  /* ---------- Notification ---------- */
  T.toast = (message) => {
    const el = $('[data-toast]');
    if (!el || !message) return;
    el.textContent = message;
    el.hidden = false;
    el.style.animation = 'none';
    void el.offsetWidth;
    el.style.animation = '';
    clearTimeout(el._t);
    el._t = setTimeout(() => (el.hidden = true), 3800);
  };

  /* ==========================================================================
     Apparition au défilement
     ========================================================================== */
  const revealObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              entry.target.classList.add('is-revealed');
              revealObserver.unobserve(entry.target);
            });
          },
          { rootMargin: '0px 0px -6% 0px', threshold: 0.08 }
        )
      : null;

  function initReveal(root = document) {
    $$('[data-reveal]:not(.is-revealed)', root).forEach((el) => {
      if (!revealObserver || !animOn() || el.closest('.tylps-no-anim')) {
        el.classList.add('is-revealed');
        return;
      }
      revealObserver.observe(el);
    });
  }

  /* ==========================================================================
     Parallaxe (sans trou visible : la translation est bornée par l'échelle)
     ========================================================================== */
  const parallaxEls = new Set();
  let parallaxTicking = false;

  function updateParallax() {
    parallaxTicking = false;
    const vh = window.innerHeight;
    parallaxEls.forEach((el) => {
      if (!el.isConnected) return parallaxEls.delete(el);
      const box = el.parentElement.getBoundingClientRect();
      if (box.bottom < -50 || box.top > vh + 50) return;
      const speed = parseFloat(el.dataset.parallax) || 0.2;
      const scale = 1 + speed;
      const maxShift = ((scale - 1) * box.height) / 2;
      const shift = Math.max(-maxShift, Math.min(maxShift, -(box.top + box.height / 2 - vh / 2) * speed));
      el.style.transform = `translate3d(0, ${shift.toFixed(1)}px, 0) scale(${scale})`;
    });
  }
  const requestParallax = () => {
    if (!parallaxTicking) {
      parallaxTicking = true;
      requestAnimationFrame(updateParallax);
    }
  };
  function initParallax(root = document) {
    if (reduceMotion || !document.body.classList.contains('tylps-parallax-on')) return;
    $$('[data-parallax]', root).forEach((el) => parallaxEls.add(el));
    requestParallax();
  }
  window.addEventListener('scroll', requestParallax, { passive: true });
  window.addEventListener('resize', requestParallax);

  /* ==========================================================================
     En-tête
     ========================================================================== */
  function initHeader() {
    const header = $('.tylps-header');
    if (!header) return;
    const section = header.closest('.shopify-section') || header.parentElement;
    // Le tiroir du menu est déplacé dans <body> pour ne jamais hériter
    // d'une transformation de l'en-tête (masquage au défilement).
    $$('[data-move-to-body]', header).forEach((drawer) => {
      $$(`body > #${drawer.id}`).forEach((old) => old !== drawer && old.remove());
      document.body.appendChild(drawer);
    });
    const setHeight = () => document.documentElement.style.setProperty('--header-height', `${header.offsetHeight}px`);
    setHeight();
    if (window.ResizeObserver) new ResizeObserver(setHeight).observe(header);

    const mode = header.dataset.sticky;
    let lastY = window.scrollY;
    if (T._headerScroll) window.removeEventListener('scroll', T._headerScroll);
    T._headerScroll = () => {
      const y = window.scrollY;
      header.classList.toggle('is-scrolled', y > 8);
      if (mode === 'on-scroll-up') {
        const hide = y > lastY && y > header.offsetHeight * 2 && !header.classList.contains('is-menu-open');
        section.classList.toggle('is-hidden', hide);
      }
      lastY = y;
    };
    window.addEventListener('scroll', T._headerScroll, { passive: true });
    T._headerScroll();

    $$('[data-dropdown]', header).forEach((d) => {
      d.addEventListener('mouseenter', () => canHover() && (d.open = true));
      d.addEventListener('mouseleave', () => canHover() && (d.open = false));
    });
  }
  document.addEventListener('click', (e) => {
    $$('[data-dropdown][open]').forEach((d) => {
      if (!d.contains(e.target)) d.open = false;
    });
  });

  /* ==========================================================================
     Barre d'annonce
     ========================================================================== */
  function initAnnouncements(root = document) {
    $$('[data-rotator]', root).forEach((rotator) => {
      clearInterval(rotator._timer);
      const items = $$('.tylps-announcement__item', rotator);
      if (!items.length) return;
      items.forEach((it) => it.classList.remove('is-active', 'is-leaving'));
      items[0].classList.add('is-active');
      if (items.length < 2 || reduceMotion) return;
      let i = 0;
      const speed = (parseInt(rotator.dataset.speed, 10) || 5) * 1000;
      rotator._timer = setInterval(() => {
        if (document.hidden) return;
        const current = items[i];
        current.classList.remove('is-active');
        current.classList.add('is-leaving');
        setTimeout(() => current.classList.remove('is-leaving'), 700);
        i = (i + 1) % items.length;
        items[i].classList.add('is-active');
      }, speed);
    });
  }

  /* ==========================================================================
     Tiroirs (menu mobile + panier)
     ========================================================================== */
  const Drawer = (T.Drawer = {
    open(id, opener) {
      const drawer = document.getElementById(id);
      if (!drawer) return false;
      drawer._opener = opener || document.activeElement;
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('tylps-lock');
      if (id === 'tylps-menu-drawer') $('.tylps-header')?.classList.add('is-menu-open');
      if (id === 'tylps-cart-drawer') {
        const rewards = drawer.querySelector('[data-rewards]');
        const pending = T.Cart?.pending;
        if (rewards) setTimeout(() => animateRewards(rewards, pending ? pending.from : 0, pending ? pending.celebrate : false), 280);
        if (T.Cart) T.Cart.pending = null;
      }
      setTimeout(() => (drawer.querySelector('[data-autofocus]') || drawer.querySelector('.tylps-close'))?.focus({ preventScroll: true }), 60);
      return true;
    },
    close(drawer) {
      if (typeof drawer === 'string') drawer = document.getElementById(drawer);
      if (!drawer || !drawer.classList.contains('is-open')) return;
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      if (!$('.tylps-drawer.is-open')) document.documentElement.classList.remove('tylps-lock');
      if (drawer.id === 'tylps-menu-drawer') $('.tylps-header')?.classList.remove('is-menu-open');
      drawer._opener?.focus?.({ preventScroll: true });
    },
  });

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      if (Drawer.open(opener.dataset.drawerOpen, opener)) e.preventDefault();
      return;
    }
    const closer = e.target.closest('[data-drawer-close]');
    if (closer) {
      e.preventDefault();
      Drawer.close(closer.closest('.tylps-drawer'));
    }
  });

  document.addEventListener('keydown', (e) => {
    const open = $('.tylps-drawer.is-open');
    if (!open) return;
    if (e.key === 'Escape') return Drawer.close(open);
    if (e.key !== 'Tab') return;
    const focusables = $$('a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])', open).filter((el) => el.offsetParent !== null);
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  });

  /* ==========================================================================
     Panier AJAX (Section Rendering API)
     ========================================================================== */
  /* Panier : date de livraison estimée, barre de récompenses animée, célébration des paliers */
  const addBusinessDays = (date, n) => {
    const d = new Date(date);
    let added = 0;
    while (added < n) {
      d.setDate(d.getDate() + 1);
      const day = d.getDay();
      if (day !== 0 && day !== 6) added += 1;
    }
    return d;
  };
  function initCartExtras(afterUpdate = false) {
    const lang = document.documentElement.lang || 'fr';
    const fmt = new Intl.DateTimeFormat(lang, { weekday: 'short', day: 'numeric', month: 'short' });
    $$('[data-eta]').forEach((el) => {
      const min = parseInt(el.dataset.min, 10) || 2;
      const max = Math.max(min, parseInt(el.dataset.max, 10) || min);
      const now = new Date();
      const start = fmt.format(addBusinessDays(now, min));
      const end = fmt.format(addBusinessDays(now, max));
      const out = el.querySelector('[data-eta-text]');
      const safe = (t) => t.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));
      out.innerHTML = safe(el.dataset.text || '')
        .replace('[start]', `<strong>${safe(start)}</strong>`)
        .replace('[end]', `<strong>${safe(end)}</strong>`);
      el.hidden = false;
    });
    const unlockedOf = (el) => parseInt(el.dataset.unlocked, 10) || 0;
    const prevPct = afterUpdate ? Cart.lastPct ?? 0 : 0;
    const prevUnlocked = Cart.lastUnlocked;
    $$('[data-rewards]').forEach((rewards) => {
      const celebrate = afterUpdate && prevUnlocked != null && unlockedOf(rewards) > prevUnlocked;
      const drawer = rewards.closest('.tylps-drawer');
      if (drawer && !drawer.classList.contains('is-open')) {
        // tiroir fermé : l'animation sera jouée à son ouverture
        Cart.pending = { from: prevPct, celebrate };
        return;
      }
      animateRewards(rewards, prevPct, celebrate);
    });
    const any = $('[data-rewards]');
    if (any) {
      Cart.lastUnlocked = unlockedOf(any);
      Cart.lastPct = parseFloat(any.dataset.pct) || 0;
    }
  }
  function animateRewards(rewards, from, celebrate) {
    const fill = rewards.querySelector('[data-rewards-fill]');
    const pct = parseFloat(rewards.dataset.pct) || 0;
    if (fill && !reduceMotion) {
      fill.style.transition = 'none';
      fill.style.setProperty('--progress', `${from}%`);
      void fill.offsetWidth;
      fill.style.transition = '';
      requestAnimationFrame(() => requestAnimationFrame(() => fill.style.setProperty('--progress', `${pct}%`)));
    }
    if (!celebrate || reduceMotion) return;
    setTimeout(() => {
      rewards.classList.add('is-celebrating');
      const burst = document.createElement('div');
      burst.className = 'tylps-confetti';
      const colors = ['var(--color-accent)', '#E3D8C6', '#161514', '#D9A273'];
      for (let i = 0; i < 18; i += 1) {
        const c = document.createElement('i');
        const angle = (Math.PI * 2 * i) / 18;
        const dist = 60 + Math.random() * 60;
        c.style.setProperty('--x', `${Math.cos(angle) * dist}px`);
        c.style.setProperty('--y', `${Math.sin(angle) * dist * 0.6 - 20}px`);
        c.style.setProperty('--r', `${Math.random() * 540 - 270}deg`);
        c.style.setProperty('--c', colors[i % colors.length]);
        c.style.animationDelay = `${Math.random() * 120}ms`;
        burst.appendChild(c);
      }
      rewards.appendChild(burst);
      setTimeout(() => { burst.remove(); rewards.classList.remove('is-celebrating'); }, 1200);
    }, 650);
  }
  T.initCartExtras = initCartExtras;

  const parser = new DOMParser();
  const Cart = (T.Cart = {
    sectionIds() {
      const ids = [];
      if (document.getElementById('tylps-cart-drawer')) ids.push('tylps-cart-drawer');
      $$('[data-cart-section]').forEach((el) => ids.push(el.dataset.cartSection));
      return ids;
    },
    async request(url, body, isForm = false) {
      const ids = this.sectionIds();
      let options;
      if (isForm) {
        if (ids.length) {
          body.append('sections', ids.join(','));
          body.append('sections_url', window.location.pathname);
        }
        options = { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body };
      } else {
        options = {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ ...body, sections: ids, sections_url: window.location.pathname }),
        };
      }
      const res = await fetch(`${url}.js`, options);
      let data;
      try {
        data = await res.json();
      } catch (e) {
        throw new Error(T.strings.cartError);
      }
      if (!res.ok || data.status) throw new Error(data.description || data.message || T.strings.cartError);
      if (data.sections) this.render(data.sections);
      return data;
    },
    add(formData) {
      return this.request(T.routes.cartAdd, formData, true);
    },
    change(key, quantity) {
      return this.request(T.routes.cartChange, { id: key, quantity });
    },
    render(sections) {
      const oldRewards = $('[data-rewards]');
      if (oldRewards) {
        Cart.lastPct = parseFloat(oldRewards.dataset.pct) || 0;
        Cart.lastUnlocked = parseInt(oldRewards.dataset.unlocked, 10) || 0;
      }
      Object.entries(sections).forEach(([id, html]) => {
        if (!html) return;
        const doc = parser.parseFromString(html, 'text/html');
        const selector = id === 'tylps-cart-drawer' ? '[data-cart-drawer-inner]' : `[data-cart-section="${id}"] [data-cart-section-inner]`;
        const fresh = doc.querySelector(id === 'tylps-cart-drawer' ? '[data-cart-drawer-inner]' : '[data-cart-section-inner]');
        const current = $(selector);
        if (fresh && current) current.replaceWith(fresh);
        const count = doc.querySelector('[data-item-count]');
        if (count) this.updateCount(parseInt(count.dataset.itemCount, 10));
      });
      initReveal();
      initCartExtras(true);
    },
    async refreshCount() {
      try {
        const res = await fetch(`${T.routes.cart}.js`, { headers: { Accept: 'application/json' } });
        const cart = await res.json();
        this.updateCount(cart.item_count);
      } catch (e) {}
    },
    updateCount(count) {
      $$('[data-cart-count]').forEach((el) => {
        const changed = String(count) !== el.dataset.count;
        el.dataset.count = count;
        el.textContent = count;
        if (changed && count > 0) {
          el.classList.remove('is-bumping');
          void el.offsetWidth;
          el.classList.add('is-bumping');
        }
      });
    },
  });

  /* Ajout au panier depuis n'importe quel formulaire produit */
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[data-tylps-product-form]');
    if (!form) return;
    const hasDrawer = !!document.getElementById('tylps-cart-drawer');
    if (!hasDrawer) return; // panier en page : soumission classique vers /cart
    e.preventDefault();
    const button = form.querySelector('[data-add-button]');
    if (!button || button.disabled || button.classList.contains('is-loading')) return;
    const inDrawer = !!form.closest('#tylps-cart-drawer');
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');
    try {
      await Cart.add(new FormData(form));
      if (inDrawer) return; // ajout depuis le panier : il se met à jour sur place
      button.classList.add('is-added');
      setTimeout(() => button.classList.remove('is-added'), 1800);
      setTimeout(() => Drawer.open('tylps-cart-drawer', button), reduceMotion ? 0 : document.body.classList.contains('tylps-fly-on') ? 780 : 450);
      document.dispatchEvent(new CustomEvent('tylps:cart:added', { detail: { button } }));
    } catch (err) {
      T.toast(err.message);
    } finally {
      button.classList.remove('is-loading');
      button.removeAttribute('aria-busy');
    }
  });

  /* Quantités et suppression (tiroir + page panier) */
  const changeLine = async (control, quantity) => {
    const line = control.closest('[data-line-key]');
    if (!line) return;
    line.classList.add(quantity <= 0 ? 'is-removing' : 'is-updating');
    if (quantity <= 0 && !reduceMotion) await new Promise((r) => setTimeout(r, 260));
    try {
      await Cart.change(line.dataset.lineKey, Math.max(0, quantity));
    } catch (err) {
      T.toast(err.message);
      line.classList.remove('is-updating', 'is-removing');
    }
  };
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-qty-step]');
    if (btn && btn.closest('[data-line-key]')) {
      e.preventDefault();
      const input = btn.closest('.tylps-qty').querySelector('input');
      changeLine(btn, (parseInt(input.value, 10) || 0) + parseInt(btn.dataset.qtyStep, 10));
      return;
    }
    if (btn) {
      // Sélecteur de quantité produit (hors panier)
      e.preventDefault();
      const input = btn.closest('.tylps-qty').querySelector('input');
      const min = parseInt(input.min, 10) || 1;
      input.value = Math.max(min, (parseInt(input.value, 10) || min) + parseInt(btn.dataset.qtyStep, 10));
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }
    const remove = e.target.closest('[data-line-remove]');
    if (remove) {
      e.preventDefault();
      changeLine(remove, 0);
    }
  });
  document.addEventListener('change', (e) => {
    const input = e.target.closest('[data-line-key] .tylps-qty input');
    if (input) changeLine(input, parseInt(input.value, 10) || 0);
  });

  /* ==========================================================================
     Accordéon animé
     ========================================================================== */
  class TylpsAccordion extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.single = this.hasAttribute('data-single');
      $$(':scope > details, :scope > [data-accordion-group] > details', this).forEach((d) => {
        if (d.open) d.classList.add('is-open');
        d.querySelector('summary').addEventListener('click', (e) => {
          e.preventDefault();
          this.toggle(d);
        });
      });
    }
    toggle(d, force) {
      const open = typeof force === 'boolean' ? force : !d.classList.contains('is-open');
      const content = d.querySelector('.tylps-accordion__content');
      if (open && this.single) {
        $$('details.is-open', this).forEach((other) => other !== d && this.toggle(other, false));
      }
      d.classList.toggle('is-open', open);
      d.querySelector('summary').setAttribute('aria-expanded', open);
      d._anim?.cancel();
      if (reduceMotion || !content || !content.animate) {
        d.open = open;
        return;
      }
      if (open) {
        d.open = true;
        const h = content.scrollHeight;
        d._anim = content.animate([{ height: '0px', opacity: 0 }, { height: `${h}px`, opacity: 1 }], { duration: 480, easing: 'cubic-bezier(0.22, 0.75, 0.15, 1)' });
        d._anim.onfinish = () => (d._anim = null);
      } else {
        const h = content.offsetHeight;
        d._anim = content.animate([{ height: `${h}px`, opacity: 1 }, { height: '0px', opacity: 0 }], { duration: 380, easing: 'cubic-bezier(0.65, 0, 0.35, 1)' });
        d._anim.onfinish = () => {
          d.open = false;
          d._anim = null;
        };
      }
    }
  }
  if (!customElements.get('tylps-accordion')) customElements.define('tylps-accordion', TylpsAccordion);

  /* ==========================================================================
     Carrousel (défilement natif + glisser à la souris + lecture auto)
     ========================================================================== */
  class TylpsCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      if (!this.track || this._ready) return;
      this._ready = true;
      this.bar = this.querySelector('[data-progress]');
      this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(-1, true));
      this.querySelector('[data-next]')?.addEventListener('click', () => this.go(1, true));
      this.track.addEventListener('scroll', () => this.update(), { passive: true });
      this.track.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); this.go(1, true); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(-1, true); }
      });
      this.initDrag();
      window.addEventListener('resize', () => this.update());
      this.update();

      this.autoplay = parseFloat(this.dataset.autoplay || 0);
      if (this.autoplay && !reduceMotion && !T.designMode) {
        this.io = new IntersectionObserver(([entry]) => (entry.isIntersecting ? this.start() : this.stop()));
        this.io.observe(this);
        this.addEventListener('pointerenter', () => this.stop());
        this.addEventListener('pointerleave', () => !this.userStopped && this.start());
        this.addEventListener('focusin', () => this.stop());
        this.addEventListener('touchstart', () => { this.userStopped = true; this.stop(); }, { passive: true });
      }
    }
    disconnectedCallback() {
      this.stop();
      this.io?.disconnect();
    }
    get slides() { return Array.from(this.track.children); }
    get step() {
      const s = this.slides;
      return s.length > 1 ? s[1].offsetLeft - s[0].offsetLeft : this.track.clientWidth;
    }
    go(dir, user) {
      if (user) this.userStopped = true;
      const t = this.track;
      const max = t.scrollWidth - t.clientWidth;
      let target = t.scrollLeft + dir * this.step;
      if (dir > 0 && t.scrollLeft >= max - 4) target = 0;
      else if (dir < 0 && t.scrollLeft <= 4) target = max;
      t.scrollTo({ left: target, behavior: reduceMotion ? 'auto' : 'smooth' });
    }
    goTo(index) {
      const s = this.slides[index];
      if (s) this.track.scrollTo({ left: s.offsetLeft - this.slides[0].offsetLeft, behavior: 'smooth' });
    }
    start() {
      this.stop();
      this.timer = setInterval(() => !document.hidden && this.go(1), this.autoplay * 1000);
    }
    stop() { clearInterval(this.timer); }
    update() {
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      const max = scrollWidth - clientWidth;
      this.classList.toggle('is-static', max <= 4);
      if (!this.bar) return;
      const size = Math.max(8, Math.min(100, (clientWidth / scrollWidth) * 100));
      const progress = max > 0 ? scrollLeft / max : 0;
      this.bar.style.setProperty('--carousel-size', `${size}%`);
      this.bar.style.setProperty('--carousel-offset', `${progress * ((100 - size) / size) * 100}%`);
    }
    initDrag() {
      const t = this.track;
      let down = false, moved = false, startX = 0, startScroll = 0;
      t.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        down = true; moved = false; startX = e.clientX; startScroll = t.scrollLeft;
      });
      t.addEventListener('pointermove', (e) => {
        if (!down) return;
        const dx = e.clientX - startX;
        if (!moved && Math.abs(dx) > 5) {
          moved = true;
          this.userStopped = true;
          t.classList.add('is-dragging');
          t.setPointerCapture(e.pointerId);
        }
        if (moved) t.scrollLeft = startScroll - dx;
      });
      const end = () => {
        if (!down) return;
        down = false;
        if (!moved) return;
        t.classList.remove('is-dragging');
        const index = Math.round((t.scrollLeft) / this.step);
        t.scrollTo({ left: index * this.step, behavior: 'smooth' });
        setTimeout(() => (moved = false), 50);
      };
      t.addEventListener('pointerup', end);
      t.addEventListener('pointercancel', end);
      t.addEventListener('click', (e) => { if (moved) { e.preventDefault(); e.stopPropagation(); } }, true);
      t.addEventListener('dragstart', (e) => e.preventDefault());
    }
  }
  if (!customElements.get('tylps-carousel')) customElements.define('tylps-carousel', TylpsCarousel);

  /* ==========================================================================
     Points forts produit (hotspots + liste synchronisée)
     ========================================================================== */
  class TylpsFeatures extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.items = $$('[data-feature]', this);
      this.pins = $$('[data-pin]', this);
      if (!this.items.length) return;
      this.items.forEach((item, i) => {
        item.addEventListener('mouseenter', () => canHover() && this.activate(i, true));
        item.querySelector('[data-feature-trigger]')?.addEventListener('click', () => this.activate(i, true));
      });
      this.pins.forEach((pin) => pin.addEventListener('click', () => this.activate(parseInt(pin.dataset.pin, 10), true)));
      this.activate(0);
      const autoplay = parseFloat(this.dataset.autoplay || 0);
      if (autoplay && !reduceMotion && !T.designMode) {
        this.io = new IntersectionObserver(([entry]) => (this.visible = entry.isIntersecting), { threshold: 0.3 });
        this.io.observe(this);
        this.timer = setInterval(() => {
          if (this.visible && !this.userActed && !document.hidden) this.activate((this.index + 1) % this.items.length);
        }, autoplay * 1000);
      }
    }
    disconnectedCallback() {
      clearInterval(this.timer);
      this.io?.disconnect();
    }
    activate(i, user) {
      if (user) {
        this.userActed = true;
        this.classList.add('is-manual');
      }
      this.index = i;
      this.items.forEach((item, j) => {
        item.classList.toggle('is-active', j === i);
        item.querySelector('[data-feature-trigger]')?.setAttribute('aria-expanded', j === i);
      });
      this.pins.forEach((pin) => pin.classList.toggle('is-active', parseInt(pin.dataset.pin, 10) === i));
      let detail = false;
      $$('[data-feature-image]', this).forEach((img) => {
        const active = parseInt(img.dataset.featureImage, 10) === i;
        img.classList.toggle('is-active', active);
        if (active) detail = true;
      });
      this.classList.toggle('is-detail', detail);
    }
  }

  /* Halo lumineux qui suit le curseur sur les cartes */
  document.addEventListener('pointermove', (e) => {
    const card = e.target.closest?.('[data-spotlight]');
    if (!card || e.pointerType !== 'mouse') return;
    const r = card.getBoundingClientRect();
    card.style.setProperty('--mx', `${e.clientX - r.left}px`);
    card.style.setProperty('--my', `${e.clientY - r.top}px`);
  }, { passive: true });
  if (!customElements.get('tylps-features')) customElements.define('tylps-features', TylpsFeatures);

  /* ==========================================================================
     Comparatif : choix du concurrent sur mobile
     ========================================================================== */
  document.addEventListener('click', (e) => {
    const tab = e.target.closest('[data-compare-tab]');
    if (!tab) return;
    const table = tab.closest('.tylps-compare');
    table.dataset.show = tab.dataset.compareTab;
    $$('[data-compare-tab]', table).forEach((t) => t.setAttribute('aria-pressed', t === tab));
  });

  /* ==========================================================================
     FAQ : recherche instantanée
     ========================================================================== */
  document.addEventListener('input', (e) => {
    const input = e.target.closest('[data-faq-search]');
    if (!input) return;
    const scope = input.closest('.tylps-faq');
    const q = input.value.trim().toLowerCase();
    let shown = 0;
    $$('.tylps-accordion__item', scope).forEach((item) => {
      const match = !q || item.textContent.toLowerCase().includes(q);
      item.hidden = !match;
      if (match) shown++;
    });
    const empty = $('[data-faq-empty]', scope);
    if (empty) empty.hidden = shown > 0;
  });

  /* ==========================================================================
     Newsletter : validation animée
     ========================================================================== */
  document.addEventListener(
    'submit',
    (e) => {
      const form = e.target.closest('[data-newsletter-form]');
      if (!form) return;
      const wrap = form.closest('.tylps-newsletter') || form;
      const input = form.querySelector('input[type="email"]');
      const error = wrap.querySelector('[data-newsletter-error]');
      const valid = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test((input?.value || '').trim());
      wrap.classList.remove('is-invalid');
      if (!valid) {
        e.preventDefault();
        void wrap.offsetWidth;
        wrap.classList.add('is-invalid');
        if (error) error.textContent = T.strings.emailInvalid || '';
        input?.focus();
        return;
      }
      if (error) error.textContent = '';
      wrap.classList.add('is-loading');
    },
    true
  );

  /* ==========================================================================
     Vidéos : lecture automatique discrète quand visibles
     ========================================================================== */
  const videoObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) =>
            entries.forEach((entry) => {
              const v = entry.target;
              if (entry.isIntersecting) v.play().catch(() => {});
              else v.pause();
            }),
          { threshold: 0.2 }
        )
      : null;
  function initVideos(root = document) {
    $$('[data-video-media][data-autoplay] video', root).forEach((v) => {
      v.muted = true;
      v.loop = true;
      v.playsInline = true;
      v.setAttribute('playsinline', '');
      if (reduceMotion || !videoObserver) return;
      videoObserver.observe(v);
    });
  }
  document.addEventListener('click', (e) => {
    const sound = e.target.closest('[data-sound-toggle]');
    if (sound) {
      const video = sound.closest('[data-video-wrap]')?.querySelector('video');
      if (!video) return;
      video.muted = !video.muted;
      if (!video.muted) video.play().catch(() => {});
      sound.classList.toggle('is-unmuted', !video.muted);
      sound.setAttribute('aria-pressed', !video.muted);
      return;
    }
    const play = e.target.closest('[data-play-toggle]');
    if (play) {
      const video = play.closest('[data-video-wrap]')?.querySelector('video');
      if (!video) return;
      if (video.paused) video.play().catch(() => {});
      else video.pause();
      play.classList.toggle('is-paused', video.paused);
    }
  });

  /* ==========================================================================
     Initialisation + compatibilité avec l'éditeur de thème
     ========================================================================== */
  function initAll(root = document) {
    initReveal(root);
    initParallax(root);
    initAnnouncements(root);
    initVideos(root);
    initCartExtras(false);
  }

  const boot = () => {
    initHeader();
    initAll();
    if (!$('.tylps-header') && document.getElementById('tylps-cart-drawer')) Cart.refreshCount();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  window.addEventListener('pageshow', (e) => {
    if (e.persisted) Cart.refreshCount();
  });

  document.addEventListener('shopify:section:load', (e) => {
    if (e.target.querySelector('.tylps-header')) initHeader();
    initAll(e.target);
    $$('[data-reveal]', e.target).forEach((el) => el.classList.add('is-revealed'));
  });

  document.addEventListener('shopify:block:select', (e) => {
    const block = e.target;
    $$('[data-reveal]', block.closest('.shopify-section') || document).forEach((el) => el.classList.add('is-revealed'));
    const slide = block.closest('.tylps-carousel__slide');
    if (slide) {
      const carousel = slide.closest('tylps-carousel');
      carousel?.stop();
      carousel?.goTo(Array.from(slide.parentElement.children).indexOf(slide));
    }
    const details = block.matches('details') ? block : block.querySelector('details');
    const accordion = details?.closest('tylps-accordion');
    if (accordion) accordion.toggle(details, true);
    const feature = block.closest('[data-feature]');
    const features = feature?.closest('tylps-features');
    if (features) features.activate(features.items.indexOf(feature), true);
  });

  document.addEventListener('shopify:section:select', (e) => {
    $$('[data-reveal]', e.target).forEach((el) => el.classList.add('is-revealed'));
  });
})();
